import React, { useCallback, useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Sparkles, ShieldAlert, Activity, Zap, RefreshCw, Globe2, Shield } from "lucide-react";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid, BarChart, Bar } from "recharts";
import { toast } from "sonner";

const sevClass = (s) => `sev-${s}`;

function Card({ children, className = "", testid }) {
  return (
    <div data-testid={testid} className={`border border-zinc-800 bg-zinc-900/60 rounded-sm ${className}`}>
      {children}
    </div>
  );
}

function WidgetTitle({ children, right }) {
  return (
    <div className="flex items-center justify-between px-4 pt-4 pb-2">
      <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-400">{children}</div>
      {right}
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [traffic, setTraffic] = useState([]);
  const [threats, setThreats] = useState([]);
  const [healing, setHealing] = useState([]);
  const [ai, setAi] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, t, th, h] = await Promise.all([
        api.get("/threats/stats"),
        api.get("/traffic/throughput"),
        api.get("/threats", { params: { limit: 12 } }),
        api.get("/self-healing/actions", { params: { limit: 8 } }),
      ]);
      setStats(s.data && typeof s.data === "object" ? s.data : null);
      setTraffic(Array.isArray(t.data) ? t.data : []);
      setThreats(Array.isArray(th.data) ? th.data : []);
      setHealing(Array.isArray(h.data) ? h.data : []);
    } catch (err) {
      console.error("dashboard load failed", err);
      setTraffic([]); setThreats([]); setHealing([]);
    }
  }, []);

  useEffect(() => {
    load();
    const i = setInterval(load, 4000);
    return () => clearInterval(i);
  }, [load]);

  const runAI = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/analyze");
      setAi(data);
      toast.success("AI analysis ready");
    } catch {
      toast.error("AI analysis failed");
    } finally {
      setAiBusy(false);
    }
  };

  const sevData = useMemo(() => {
    const s = stats?.by_severity || {};
    return [
      { name: "low", v: s.low || 0, fill: "#71717a" },
      { name: "medium", v: s.medium || 0, fill: "#f59e0b" },
      { name: "high", v: s.high || 0, fill: "#fb923c" },
      { name: "critical", v: s.critical || 0, fill: "#ef4444" },
    ];
  }, [stats]);

  return (
    <Layout title="Operations Overview" subtitle="DASHBOARD">
      {/* KPI bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        {[
          { l: "TOTAL EVENTS", v: stats?.total ?? "—", icon: Activity, c: "text-cyan-400" },
          { l: "BLOCKED", v: stats?.blocked ?? "—", icon: ShieldAlert, c: "text-red-400" },
          { l: "AUTO-HEALED", v: healing.length, icon: Zap, c: "text-amber-400" },
          { l: "ATTACK ORIGINS", v: stats?.by_country?.length ?? 0, icon: Globe2, c: "text-emerald-400" },
        ].map((k) => {
          const I = k.icon;
          return (
            <Card key={k.l} testid={`kpi-${k.l.toLowerCase().replace(/ /g, "-")}`} className="p-4 fade-up">
              <div className="flex items-center justify-between">
                <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500">{k.l}</div>
                <I className={`w-4 h-4 ${k.c}`} />
              </div>
              <div className="font-display text-3xl font-bold mt-2 tabular-nums">{k.v}</div>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* Traffic chart */}
        <Card testid="traffic-chart" className="col-span-12 lg:col-span-8 fade-up">
          <WidgetTitle right={<span className="text-[10px] font-mono text-emerald-400 flex items-center gap-2"><span className="live-dot" /> LIVE</span>}>
            LIVE TRAFFIC THROUGHPUT (Mbps)
          </WidgetTitle>
          <div className="h-64 px-2 pb-2">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={traffic}>
                <defs>
                  <linearGradient id="gIn" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#06b6d4" stopOpacity={0.6} />
                    <stop offset="100%" stopColor="#06b6d4" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gOut" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#f59e0b" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <XAxis dataKey="label" tick={{ fill: "#71717a", fontSize: 10, fontFamily: "JetBrains Mono" }} stroke="#3f3f46" />
                <YAxis tick={{ fill: "#71717a", fontSize: 10, fontFamily: "JetBrains Mono" }} stroke="#3f3f46" />
                <Tooltip contentStyle={{ background: "#09090b", border: "1px solid #27272a", borderRadius: 2, fontFamily: "JetBrains Mono", fontSize: 11 }} />
                <Area type="monotone" dataKey="inbound_mbps" stroke="#06b6d4" fill="url(#gIn)" strokeWidth={1.5} />
                <Area type="monotone" dataKey="outbound_mbps" stroke="#f59e0b" fill="url(#gOut)" strokeWidth={1.5} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* AI panel */}
        <Card testid="ai-panel" className="col-span-12 lg:col-span-4 beam-border bg-zinc-900/80 fade-up">
          <WidgetTitle right={<Sparkles className="w-3.5 h-3.5 text-cyan-400" />}>AI ANOMALY ANALYSIS</WidgetTitle>
          <div className="px-4 pb-4 space-y-3">
            <div className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">Claude Sonnet 4.5</div>
            <div className="min-h-[140px] text-xs leading-relaxed text-zinc-300 whitespace-pre-wrap font-mono" data-testid="ai-summary">
              {aiBusy ? "[ analyzing recent threat events... ]" : ai?.summary || "Run AI analysis to summarize anomalies across the last 40 events. The model will surface patterns, hotspots, and probable attack chains."}
            </div>
            <Button
              data-testid="run-ai-btn"
              onClick={runAI} disabled={aiBusy}
              className="w-full bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm font-semibold gap-2"
            >
              <Sparkles className="w-4 h-4" />
              {aiBusy ? "ANALYZING..." : "RUN ANALYSIS"}
            </Button>
          </div>
        </Card>

        {/* Live event stream */}
        <Card testid="live-stream" className="col-span-12 lg:col-span-7 fade-up">
          <WidgetTitle right={<span className="text-[10px] font-mono text-emerald-400 flex items-center gap-2"><span className="live-dot" /> STREAMING</span>}>
            REAL-TIME THREAT STREAM
          </WidgetTitle>
          <div className="overflow-hidden">
            <div className="max-h-80 overflow-y-auto font-mono text-[11px]">
              {threats.map((t) => (
                <div key={t.threat_id} className="grid grid-cols-12 gap-2 px-4 py-2 border-t border-zinc-800/70 hover:bg-zinc-900/80">
                  <div className="col-span-2 text-zinc-500">{(t.timestamp || "").slice(11, 19)}</div>
                  <div className={`col-span-2 inline-flex justify-center px-1.5 py-0.5 border rounded-sm uppercase tracking-widest text-[10px] ${sevClass(t.severity)}`}>{t.severity}</div>
                  <div className="col-span-3 text-zinc-200 whitespace-normal break-words">{t.category}</div>
                  <div className="col-span-2 text-cyan-400">{t.source_country} {t.source_ip}</div>
                  <div className="col-span-2 text-zinc-400">:{t.destination_port}</div>
                  <div className="col-span-1 text-right text-amber-400 uppercase">{(t.action_taken || "").slice(0, 7)}</div>
                </div>
              ))}
              {threats.length === 0 && <div className="text-zinc-500 px-4 py-6">awaiting events...</div>}
            </div>
          </div>
        </Card>

        {/* Severity distribution */}
        <Card testid="sev-chart" className="col-span-12 lg:col-span-5 fade-up">
          <WidgetTitle>EVENT SEVERITY DISTRIBUTION</WidgetTitle>
          <div className="h-72 px-2 pb-3">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={sevData} layout="vertical" margin={{ left: 30, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" horizontal={false} />
                <XAxis type="number" tick={{ fill: "#71717a", fontSize: 10, fontFamily: "JetBrains Mono" }} stroke="#3f3f46" />
                <YAxis type="category" dataKey="name" tick={{ fill: "#a1a1aa", fontSize: 10, fontFamily: "JetBrains Mono" }} stroke="#3f3f46" />
                <Tooltip contentStyle={{ background: "#09090b", border: "1px solid #27272a", borderRadius: 2 }} />
                <Bar dataKey="v" radius={[0, 0, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Top countries */}
        <Card testid="geo-list" className="col-span-12 lg:col-span-6 fade-up">
          <WidgetTitle right={<RefreshCw className="w-3.5 h-3.5 text-zinc-500" />}>ATTACK ORIGIN GEOGRAPHY</WidgetTitle>
          <div className="px-4 pb-4 space-y-2">
            {(stats?.by_country || []).map((c, i) => {
              const max = Math.max(1, ...(stats?.by_country || []).map((x) => x.count));
              return (
                <div key={c.country} className="flex items-center gap-3" data-testid={`country-row-${i}`}>
                  <div className="w-8 font-mono text-xs text-cyan-400">{c.country}</div>
                  <div className="flex-1 h-2 bg-zinc-800 rounded-sm overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-cyan-500 to-amber-500" style={{ width: `${(c.count / max) * 100}%` }} />
                  </div>
                  <div className="w-12 text-right font-mono text-xs text-zinc-300">{c.count}</div>
                </div>
              );
            })}
            {(!stats?.by_country || stats.by_country.length === 0) && <div className="text-zinc-500 text-sm font-mono">awaiting data...</div>}
          </div>
        </Card>

        {/* Self-healing recent */}
        <Card testid="healing-recent" className="col-span-12 lg:col-span-6 fade-up">
          <WidgetTitle>SELF-HEALING ACTIONS</WidgetTitle>
          <div className="px-2 pb-3 space-y-1.5">
            {healing.slice(0, 6).map((h) => (
              <div key={h.action_id} className="flex items-center gap-3 px-3 py-2 border border-zinc-800 rounded-sm bg-zinc-900/50">
                <span className="live-dot amber" />
                <span className="text-[10px] font-mono text-amber-400 uppercase tracking-widest">{h.type}</span>
                <span className="font-mono text-[11px] text-zinc-200 flex-1 whitespace-normal break-words">{h.target}</span>
                <span className="font-mono text-[10px] text-zinc-500 uppercase">{h.cloud}</span>
              </div>
            ))}
            {healing.length === 0 && <div className="text-zinc-500 text-sm font-mono px-3 py-3">no actions yet...</div>}
          </div>
        </Card>
      </div>
    </Layout>
  );
}
